import { FolderOpen, Plus, Search } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { createProject, getProjectsByWorkspace } from '@/client/sdk.gen'
import type { ProjectInfo } from '@/client/types.gen'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Spinner } from '@/components/ui/spinner'
import { useAuth, useDataRefresh, useWorkspace } from '@/context'
import { useTranslation } from '@/hooks/useTranslation'
import { cn } from '@/lib/utils'
import { extractErrorMessage, isErrorHandledByInterceptor } from '@/utils/error'

export interface RecordProjectPickerSheetProps {
	open: boolean
	onOpenChange: (open: boolean) => void
}

export function RecordProjectPickerSheet({ open, onOpenChange }: RecordProjectPickerSheetProps) {
	const { t } = useTranslation()
	const { user } = useAuth()
	const { selectedWorkspaceId, loading: workspaceLoading } = useWorkspace()
	const { invalidateProjects } = useDataRefresh()
	const navigate = useNavigate()

	const [projects, setProjects] = useState<ProjectInfo[]>([])
	const [loading, setLoading] = useState(false)
	const [searchQuery, setSearchQuery] = useState('')
	const [showCreateRow, setShowCreateRow] = useState(false)
	const [newProjectName, setNewProjectName] = useState('')
	const [creating, setCreating] = useState(false)

	const userWorkspaceId = (user as { workspace_id?: string } | null)?.workspace_id

	const filteredProjects = useMemo(() => {
		const q = searchQuery.trim().toLowerCase()
		if (!q) return projects
		return projects.filter((p) => p.name?.toLowerCase().includes(q))
	}, [projects, searchQuery])

	const pickProjectAndRecord = useCallback(
		(projectId: string) => {
			navigate(`/projects/${projectId}?openRecorder=1`)
			onOpenChange(false)
		},
		[navigate, onOpenChange]
	)

	useEffect(() => {
		if (!open) {
			return
		}
		setSearchQuery('')
		setShowCreateRow(false)
		setNewProjectName('')
	}, [open])

	useEffect(() => {
		if (!open || workspaceLoading) {
			return
		}

		let cancelled = false

		const load = async () => {
			setLoading(true)
			try {
				const workspaceId = selectedWorkspaceId || userWorkspaceId
				if (!workspaceId) {
					if (!cancelled) {
						setProjects([])
					}
					return
				}

				const response = await getProjectsByWorkspace({
					path: { workspace_id: workspaceId },
					query: { limit: 100 },
				})

				if (!cancelled) {
					setProjects(response.data?.projects ?? [])
				}
			} catch (error: unknown) {
				if (!cancelled) {
					const detail = extractErrorMessage(error, t('pages.projects.errors.failedToLoadProjects'))
					toast.error(detail)
				}
			} finally {
				if (!cancelled) {
					setLoading(false)
				}
			}
		}

		void load()

		return () => {
			cancelled = true
		}
	}, [open, selectedWorkspaceId, userWorkspaceId, workspaceLoading, t])

	const handleCreateProject = async () => {
		const name = newProjectName.trim()
		if (!name) return

		const workspaceId = selectedWorkspaceId || userWorkspaceId
		if (!workspaceId) {
			toast.error(t('pages.projects.errors.failedToCreateProject'))
			return
		}

		setCreating(true)
		try {
			const response = await createProject({
				body: {
					name,
					workspace_id: workspaceId,
				},
			})

			const newId = response.data?.project?.id ?? response.data?.project_id
			if (!newId) {
				toast.error(t('pages.projects.errors.failedToCreateProject'))
				return
			}

			invalidateProjects()
			toast.success(t('pages.projects.success.projectCreatedSuccessfully'))
			setNewProjectName('')
			setShowCreateRow(false)
			pickProjectAndRecord(newId)
		} catch (e: unknown) {
			if (isErrorHandledByInterceptor(e)) {
				return
			}
			toast.error(extractErrorMessage(e, t('pages.projects.errors.failedToCreateProject')))
		} finally {
			setCreating(false)
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				showCloseButton
				className={cn(
					'!fixed !bottom-0 !left-0 !right-0 !top-auto box-border grid max-h-[85vh] w-full min-w-0 max-w-full overflow-x-hidden !translate-x-0 !translate-y-0 gap-4 rounded-t-2xl rounded-b-none border-t border-border px-4 pt-5 pb-[max(1rem,env(safe-area-inset-bottom))] [&>*]:min-w-0'
				)}
			>
				<DialogHeader className="min-w-0 shrink-0 pr-10 text-left">
					<DialogTitle className="truncate">{t('pages.projectPicker.title')}</DialogTitle>
					<DialogDescription className="sr-only">
						{t('pages.projectPicker.description')}
					</DialogDescription>
				</DialogHeader>

				<div className="relative w-full min-w-0 shrink-0 overflow-hidden">
					<Search
						className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground"
						aria-hidden
					/>
					<Input
						type="search"
						autoComplete="off"
						autoCorrect="off"
						placeholder={t('pages.projectPicker.search')}
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						className="pl-10"
						aria-label={t('pages.projectPicker.search')}
					/>
				</div>

				<button
					type="button"
					onClick={() => setShowCreateRow((prev) => !prev)}
					className={cn(
						'flex w-full min-w-0 shrink-0 items-center gap-2 rounded-xl border border-dashed px-4 py-3 text-left transition',
						showCreateRow
							? 'border-brand-accent bg-brand-accent/5 text-brand-accent'
							: 'border-border text-foreground hover:border-brand-accent/60 hover:bg-brand-accent/5'
					)}
				>
					<Plus className="h-5 w-5 shrink-0" aria-hidden />
					<span className="min-w-0 truncate font-medium">{t('pages.projectPicker.createNew')}</span>
				</button>

				{showCreateRow ? (
					<div className="flex min-w-0 shrink-0 flex-col gap-2 sm:flex-row sm:items-end">
						<div className="min-w-0 flex-1 space-y-1">
							<label htmlFor="record-picker-new-project-name" className="sr-only">
								{t('pages.projectPicker.createPlaceholder')}
							</label>
							<Input
								id="record-picker-new-project-name"
								autoComplete="off"
								autoCorrect="off"
								disabled={creating}
								value={newProjectName}
								onChange={(e) => setNewProjectName(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === 'Enter') {
										e.preventDefault()
										void handleCreateProject()
									}
								}}
								placeholder={t('pages.projectPicker.createPlaceholder')}
								className="w-full"
							/>
						</div>
						<Button
							type="button"
							variant="default"
							className="bg-brand-accent text-white hover:bg-brand-accent-hover sm:w-auto"
							disabled={creating || !newProjectName.trim()}
							onClick={() => void handleCreateProject()}
						>
							{creating ? (
								<Spinner className="size-4 shrink-0" aria-hidden />
							) : (
								t('pages.projects.dialogs.new.create')
							)}
						</Button>
					</div>
				) : null}

				<div className="min-h-[12rem] min-w-0 flex-1 overflow-hidden">
					{loading ? (
						<div className="flex items-center justify-center py-16">
							<Spinner />
						</div>
					) : filteredProjects.length === 0 ? (
						<p className="py-12 text-center text-sm text-muted-foreground">
							{t('pages.projectPicker.empty')}
						</p>
					) : (
						<ScrollArea className="h-[min(40vh,20rem)] min-w-0 max-w-full pr-3 [&_[data-radix-scroll-area-viewport]]:max-w-full [&_[data-radix-scroll-area-viewport]]:min-w-0">
							<ul className="space-y-1 pb-2">
								{filteredProjects.map((project) => (
									<li key={project.id}>
										<button
											type="button"
											className={cn(
												'flex w-full min-w-0 max-w-full items-start gap-3 rounded-xl px-3 py-3 text-left transition',
												'hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'
											)}
											onClick={() => pickProjectAndRecord(project.id)}
										>
											<FolderOpen className="mt-0.5 h-6 w-6 shrink-0 text-muted-foreground" aria-hidden />
											<span className="min-w-0 flex-1 whitespace-normal break-words [overflow-wrap:anywhere] font-medium">
												{project.name}
											</span>
										</button>
									</li>
								))}
							</ul>
						</ScrollArea>
					)}
				</div>
			</DialogContent>
		</Dialog>
	)
}
